import React, { useState, useCallback, useRef, useMemo, useEffect } from "react";
import ReactDOM from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faTrashAlt,
  faMagnifyingGlass,
  faGrid2Plus,
} from "@awesome.me/kit-70c1f0055f/icons/classic/light";
import { faBookmark } from "@awesome.me/kit-70c1f0055f/icons/classic/solid";
import { motion, AnimatePresence } from "framer-motion";
import { EditablePill } from "@/components/common/EditablePill";
import { resolveColor } from "@/utils/colorUtils";
import { Button } from "@/components/ui/button/Button";
import { PREDEFINED_ICONS } from "@/components/common/IconPicker";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { ResourceList } from "../../services/api/playground";

interface ResourceListSectionProps {
  resourceLists: ResourceList[];
  activeResourceListId: string | null;
  onSelectResourceList: (resourceListId: string | null) => void;
  onCreateResourceList: () => void;
  onEditResourceList: (resourceListId: string) => void;
  onDeleteResourceList: (resourceListId: string) => void;
  onUpdateResourceListOrder?: (orderedIds: string[]) => void;
  onUpdateResourceListIcon?: (
    resourceListId: string,
    iconName: string | undefined
  ) => void;
  onUpdateResourceListColor?: (
    resourceListId: string,
    colorHex: string | undefined
  ) => void;
  onUpdateResourceList: (id: string, updates: Partial<ResourceList>) => void;
  onToggleFavourite?: (resourceListId: string, isFavourite: boolean) => void;
  onUpdateFavouriteOrder?: (order: string[]) => void;
  isDirty: boolean;
  onSave: () => void;
  onAddToDashboard?: () => void;
}

// List item in the dropdown
interface ListItemProps {
  list: ResourceList;
  isActive: boolean;
  onSelect: () => void;
  onContextMenu: (e: React.MouseEvent) => void;
  onToggleFavourite?: (id: string, isFavourite: boolean) => void;
}

function ListItem({
  list,
  isActive,
  onSelect,
  onContextMenu,
  onToggleFavourite,
}: ListItemProps) {
  const resolvedColor = resolveColor(list.color) || undefined;

  const getIconDefinitionByName = (
    name?: string
  ): IconDefinition | undefined => {
    if (!name) return undefined;
    const found = PREDEFINED_ICONS.find((icon) => icon.name === name);
    return found?.definition;
  };

  const iconDef = list.icon ? getIconDefinitionByName(list.icon) : undefined;

  return (
    <div
      className="flex items-center gap-3 px-3 py-1 hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer rounded-lg group"
      onClick={(e) => {
        if ((e.target as HTMLElement).closest(".icon-container")) {
          return;
        }
        onSelect();
      }}
      onContextMenu={onContextMenu}
    >
      {iconDef && (
        <div
          className="flex items-center justify-center w-5 h-5 flex-shrink-0"
          style={{
            color: resolvedColor || undefined,
          }}
        >
          <FontAwesomeIcon icon={iconDef} className="h-4 w-4" />
        </div>
      )}
      {!iconDef && resolvedColor && (
        <div
          className="w-3 h-3 rounded-full flex-shrink-0"
          style={{ backgroundColor: resolvedColor }}
        />
      )}
      <span
        className={`flex-1 text-sm ${
          isActive
            ? "font-semibold text-gray-900 dark:text-gray-100"
            : "font-normal text-gray-700 dark:text-gray-300"
        }`}
      >
        {list.name}
      </span>
      {onToggleFavourite && list.id && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleFavourite(list.id!, !list.is_favourite);
          }}
          className="ml-2 p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          title={
            list.is_favourite ? "Remove from favorites" : "Add to favorites"
          }
        >
          <FontAwesomeIcon
            icon={faBookmark}
            className={`h-4 w-4 transition-colors ${
              list.is_favourite
                ? "text-yellow-500 dark:text-yellow-400 fill-current"
                : "text-gray-400 dark:text-gray-500"
            }`}
          />
        </button>
      )}
    </div>
  );
}

// Sortable wrapper for favourite pills
interface SortableFavouritePillProps {
  list: ResourceList;
  isActive: boolean;
  onSelect: () => void;
  onContextMenu: (e: React.MouseEvent) => void;
  onIconChange?: (iconName: string | undefined) => void;
  onColorChange?: (colorHex: string | undefined) => void;
}

function SortableFavouritePill({
  list,
  isActive,
  onSelect,
  onContextMenu,
  onIconChange,
  onColorChange,
}: SortableFavouritePillProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: list.id!,
  });

  const style: React.CSSProperties = {
    // Use Translate instead of Transform to prevent stretching/compressing during drag
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : undefined,
  };

  const resolvedColor = resolveColor(list.color) || undefined;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="touch-none cursor-grab active:cursor-grabbing"
      onContextMenu={onContextMenu}
      {...attributes}
      {...listeners}
    >
      <EditablePill
        text={list.name}
        currentIcon={list.icon}
        currentColor={resolvedColor}
        opacityIntensity={0.6}
        flat={true}
        onClickPill={onSelect}
        onIconChange={onIconChange || (() => {})}
        onColorChange={onColorChange || (() => {})}
        isActive={isActive}
        className="whitespace-nowrap"
      />
    </div>
  );
}

export function ResourceListSection({
  resourceLists,
  activeResourceListId,
  onSelectResourceList,
  onCreateResourceList,
  onDeleteResourceList,
  onUpdateResourceListOrder,
  onUpdateResourceListIcon,
  onUpdateResourceListColor,
  onToggleFavourite: onToggleFavouriteProp,
  onUpdateFavouriteOrder,
  isDirty,
  onSave,
  onAddToDashboard,
}: ResourceListSectionProps) {
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    listId: string;
    isFavourite?: boolean;
  } | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [inputValue, setInputValue] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [localLists, setLocalLists] = useState(resourceLists);

  // State for favourite order (for optimistic UI updates during drag)
  // Backend returns favourites pre-sorted, so we start with empty array
  const [favouriteOrder, setFavouriteOrder] = useState<string[]>([]);

  // DnD sensors for drag-and-drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5, // Require 5px movement before starting drag
      },
    })
  );

  // Sync local lists with prop changes
  useEffect(() => {
    setLocalLists((prevLists) => {
      const newListsMap = new Map(resourceLists.map((list) => [list.id, list]));
      return prevLists
        .map((list) => {
          const newList = newListsMap.get(list.id);
          if (newList) {
            return {
              ...newList,
              is_favourite:
                newList.is_favourite !== undefined
                  ? newList.is_favourite
                  : list.is_favourite,
            };
          }
          return list;
        })
        .concat(
          resourceLists.filter(
            (list) => !prevLists.some((prev) => prev.id === list.id)
          )
        );
    });
  }, [resourceLists]);

  // Update input value when active list changes
  useEffect(() => {
    const activeList = localLists.find(
      (list) => list.id === activeResourceListId
    );
    if (activeList) {
      setInputValue(activeList.name);
    } else {
      setInputValue("");
    }
  }, [activeResourceListId, localLists]);

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
        setSearchQuery("");
        const activeList = localLists.find(
          (list) => list.id === activeResourceListId
        );
        setInputValue(activeList ? activeList.name : "");
      }
    };

    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isDropdownOpen, activeResourceListId, localLists]);

  // Handle context menu click outside
  useEffect(() => {
    const handleClick = () => setContextMenu(null);
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, []);

  const handleContextMenu = (
    e: React.MouseEvent,
    listId: string,
    isFavourite?: boolean
  ) => {
    e.preventDefault();
    e.stopPropagation();

    const menuWidth = 200;
    const menuHeight = 100;
    const padding = 5;
    const offset = 2;

    let x = e.clientX + offset;
    let y = e.clientY + offset;

    if (x + menuWidth > window.innerWidth) {
      x = e.clientX - menuWidth - offset;
    }

    if (y + menuHeight > window.innerHeight) {
      y = e.clientY - menuHeight - offset;
    }

    x = Math.max(padding, x);
    y = Math.max(padding, y);

    setContextMenu({
      x,
      y,
      listId,
      isFavourite,
    });
  };

  const filteredLists = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    if (!query) {
      return localLists;
    }
    return localLists.filter((list) => list.name.toLowerCase().includes(query));
  }, [localLists, searchQuery]);

  const favoriteLists = useMemo(() => {
    const filtered = localLists.filter((list) => list.is_favourite);

    // Sort by favouriteOrder if available
    if (favouriteOrder.length > 0) {
      const orderMap = new Map(favouriteOrder.map((id, index) => [id, index]));
      filtered.sort((a, b) => {
        const aIndex = orderMap.get(a.id!) ?? Infinity;
        const bIndex = orderMap.get(b.id!) ?? Infinity;
        return aIndex - bIndex;
      });
    }

    return filtered.slice(0, 5);
  }, [localLists, favouriteOrder]);

  // Handle drag end for reordering favourites
  const handleFavouriteDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (over && active.id !== over.id) {
        const oldIndex = favoriteLists.findIndex(
          (item) => item.id === active.id
        );
        const newIndex = favoriteLists.findIndex((item) => item.id === over.id);

        if (oldIndex !== -1 && newIndex !== -1) {
          const newOrder = arrayMove(
            favoriteLists.map((f) => f.id!),
            oldIndex,
            newIndex
          );
          // Optimistic UI update
          setFavouriteOrder(newOrder);

          // Persist to backend via API
          onUpdateFavouriteOrder?.(newOrder);
        }
      }
    },
    [favoriteLists, onUpdateFavouriteOrder]
  );

  const handleToggleFavorite = useCallback(
    (id: string, isFavourite: boolean) => {
      // Update local state immediately for optimistic UI
      setLocalLists((prevLists) =>
        prevLists.map((l) =>
          l.id === id ? { ...l, is_favourite: isFavourite } : l
        )
      );

      // Call the prop handler to persist to backend
      if (onToggleFavouriteProp) {
        onToggleFavouriteProp(id, isFavourite);
      }
    },
    [onToggleFavouriteProp]
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    setSearchQuery(value);
    setIsDropdownOpen(true);
  };

  const handleInputFocus = () => {
    setIsDropdownOpen(true);
    if (!searchQuery) {
      setSearchQuery("");
    }
  };

  const handleSelectList = (listId: string) => {
    onSelectResourceList(listId);
    setIsDropdownOpen(false);
    setSearchQuery("");
    const selectedList = localLists.find((list) => list.id === listId);
    setInputValue(selectedList ? selectedList.name : "");
  };

  const handleIconChange = useCallback(
    (listId: string, iconName: string | undefined) => {
      setLocalLists((prevLists) =>
        prevLists.map((l) => (l.id === listId ? { ...l, icon: iconName } : l))
      );
      onUpdateResourceListIcon?.(listId, iconName);
    },
    [onUpdateResourceListIcon]
  );

  const handleColorChange = useCallback(
    (listId: string, colorHex: string | undefined) => {
      setLocalLists((prevLists) =>
        prevLists.map((l) => (l.id === listId ? { ...l, color: colorHex } : l))
      );
      onUpdateResourceListColor?.(listId, colorHex);
    },
    [onUpdateResourceListColor]
  );

  return (
    <div className="py-4">
      <div className="flex gap-2 w-full items-start">
        <div
          className={`font-mono flex flex-col text-left w-24 mt-2.5 relative items-start gap-10 text-xs uppercase tracking-widest text-gray-400 dark:text-gray-400 shrink-0 ${
            favoriteLists.length > 0 ? "h-[68px]" : ""
          }`}
        >
          <div>My Lists</div>
          {favoriteLists.length > 0 && <div>Favourites</div>}
        </div>

        <div ref={dropdownRef} className="relative flex-grow min-w-0">
          <div className="relative">
            <FontAwesomeIcon
              icon={faMagnifyingGlass}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 h-4 w-4 pointer-events-none"
            />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search lists..."
              value={inputValue}
              onChange={handleInputChange}
              onFocus={handleInputFocus}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  setIsDropdownOpen(false);
                  setSearchQuery("");
                  const activeList = localLists.find(
                    (list) => list.id === activeResourceListId
                  );
                  setInputValue(activeList ? activeList.name : "");
                } else if (e.key === "Enter" && filteredLists.length > 0) {
                  handleSelectList(filteredLists[0].id!);
                }
              }}
              className="w-full pl-10 pr-3 py-2 h-9 text-sm rounded-xl bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500"
            />
          </div>

          {isDropdownOpen && filteredLists.length > 0 && (
            <div className="absolute z-50 w-full mt-2 bg-white dark:bg-slate-800 rounded-xl px-1 shadow-xl max-h-96 overflow-hidden">
              <div className="max-h-80 overflow-y-auto py-1">
                {filteredLists.map((list) => (
                  <ListItem
                    key={list.id}
                    list={list}
                    isActive={activeResourceListId === list.id}
                    onSelect={() => handleSelectList(list.id!)}
                    onContextMenu={(e) =>
                      handleContextMenu(e, list.id!, list.is_favourite)
                    }
                    onToggleFavourite={handleToggleFavorite}
                  />
                ))}
              </div>
            </div>
          )}

          {isDropdownOpen &&
            searchQuery.trim() &&
            filteredLists.length === 0 && (
              <div className="absolute z-50 w-full mt-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl overflow-hidden">
                <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
                  No lists found
                </div>
              </div>
            )}

          {favoriteLists.length > 0 && !searchQuery.trim() && (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleFavouriteDragEnd}
            >
              <SortableContext
                items={favoriteLists.map((l) => l.id!)}
                strategy={horizontalListSortingStrategy}
              >
                <div className="mt-5 flex flex-wrap gap-2 items-center">
                  {favoriteLists.map((list) => {
                    const isActive = activeResourceListId === list.id;
                    return (
                      <SortableFavouritePill
                        key={list.id}
                        list={list}
                        isActive={isActive}
                        onSelect={() => handleSelectList(list.id!)}
                        onContextMenu={(e) =>
                          handleContextMenu(e, list.id!, list.is_favourite)
                        }
                        onIconChange={(iconName) =>
                          handleIconChange(list.id!, iconName)
                        }
                        onColorChange={(colorHex) =>
                          handleColorChange(list.id!, colorHex)
                        }
                      />
                    );
                  })}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>

        <div className="flex-shrink-0 h-9 flex items-center gap-2">
          <div
            className={`transition-all duration-300 ease-in-out overflow-hidden ${
              isDirty ? "w-16" : "w-0"
            }`}
          >
            <AnimatePresence mode="wait">
              {isDirty && (
                <motion.div
                  initial={{ opacity: 0, scale: 1, x: 8 }}
                  animate={{ opacity: 1, scale: 1, x: 0 }}
                  exit={{
                    opacity: 0,
                    scale: 1,
                    x: 8,
                    transition: { duration: 0.15 },
                  }}
                  transition={{
                    type: "spring",
                    stiffness: 500,
                    damping: 30,
                    delay: 0.15,
                  }}
                  style={{ transformOrigin: "center" }}
                >
                  <Button color="indigo" onClick={onSave} className="w-full">
                    Save
                  </Button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          {onAddToDashboard && (
            <Button
              onClick={onAddToDashboard}
              aria-label="Add to dashboard"
              title="Add to dashboard"
              icon={<FontAwesomeIcon icon={faGrid2Plus} />}
              plain
            />
          )}
          {onCreateResourceList && (
            <Button
              onClick={onCreateResourceList}
              icon={<FontAwesomeIcon icon={faPlus} />}
              plain
            />
          )}
        </div>
      </div>

      {contextMenu &&
        ReactDOM.createPortal(
          <div
            className="fixed bg-white dark:bg-gray-800 rounded-md shadow-lg z-[9999] divide-y divide-gray-100 dark:divide-gray-700 min-w-[180px]"
            style={{
              left: `${contextMenu.x}px`,
              top: `${contextMenu.y}px`,
              position: "fixed",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-1">
              {contextMenu.isFavourite ? (
                <button
                  className="w-full px-3 py-1.5 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-blue-50 hover:dark:bg-gray-700 hover:text-blue-600 dark:hover:text-blue-400 flex items-center gap-2 rounded-md"
                  onClick={() => {
                    handleToggleFavorite(contextMenu.listId, false);
                    setContextMenu(null);
                  }}
                >
                  <FontAwesomeIcon
                    icon={faBookmark}
                    className="h-3.5 w-3.5 text-yellow-500"
                  />
                  <span>Remove from Favourites</span>
                </button>
              ) : (
                <button
                  className="w-full px-3 py-1.5 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-blue-50 hover:dark:bg-gray-700 hover:text-blue-600 dark:hover:text-blue-400 flex items-center gap-2 rounded-md"
                  onClick={() => {
                    handleToggleFavorite(contextMenu.listId, true);
                    setContextMenu(null);
                  }}
                >
                  <FontAwesomeIcon icon={faBookmark} className="h-3.5 w-3.5" />
                  <span>Add to Favourites</span>
                </button>
              )}
            </div>
            <div className="p-1">
              <button
                className="w-full px-3 py-1.5 text-left text-sm hover:bg-blue-50 dark:hover:bg-gray-700 flex items-center gap-2 text-red-600 dark:text-red-400 rounded-md"
                onClick={() => {
                  onDeleteResourceList(contextMenu.listId);
                  setContextMenu(null);
                }}
              >
                <FontAwesomeIcon icon={faTrashAlt} className="h-3.5 w-3.5" />
                <span>Delete List</span>
              </button>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
